import {
  FormControl,
  FormErrorMessage,
  HStack,
  IconButton,
  Input,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Tag,
  Text,
} from "@chakra-ui/react";
import React, { memo, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { FaPlusCircle } from "react-icons/fa";
import { FaCircleMinus } from "react-icons/fa6";
import { CycleTimeImprovement } from "../../contexts/types";

/**
 * サイクルタイム改善リストの1行分のProps
 */
interface Props {
  /** 対象項目 */
  item: CycleTimeImprovement;
  /** リスト内のインデックス */
  index: number;
  /** 入力エラーかどうか */
  isInvalid: boolean;
  /** エラーメッセージ（先頭行のみ表示） */
  errorMessage?: string;
  /** 選択可能な最小月 (YYYY-MM形式) */
  minMonth: string;
  /** 選択可能な最大月 (YYYY-MM形式) */
  maxMonth: string;
  /** サイクルタイムの最大値 */
  cycleTimeMax: number;
  /** 最終行かどうか（追加ボタン表示用） */
  isLast: boolean;
  /** リストが1件のみかどうか（削除ボタン非表示用） */
  isOnly: boolean;
  /** BOMから指定月のサイクルタイムを取得する関数（Map参照のみの軽量関数） */
  getCycleTimeFromBom: (targetYearMonth: string) => number | null;
  /** 月変更時のハンドラ */
  onMonthChange: (index: number, month: string) => void;
  /** サイクルタイムテキスト入力中の変更ハンドラ（表示テキストのみ更新） */
  onTextChange: (index: number, value: string) => void;
  /** フォーカスアウト時の確定ハンドラ */
  onBlurCommit: (index: number, value: string) => void;
  /** 行追加ハンドラ */
  onAdd: () => void;
  /** 行削除ハンドラ */
  onRemove: (index: number) => void;
}

/**
 * サイクルタイム改善リストの1行分の入力UI
 * ※対応D: React.memo化し、他行の入力による再レンダリングを防止する
 * @param props パラメータ
 * @returns
 */
const CycleTimeImprovementRow: React.FC<Props> = memo(({
  item,
  index,
  isInvalid,
  errorMessage,
  minMonth,
  maxMonth,
  cycleTimeMax,
  isLast,
  isOnly,
  getCycleTimeFromBom,
  onMonthChange,
  onTextChange,
  onBlurCommit,
  onAdd,
  onRemove,
}) => {
  const { t } = useTranslation();

  // console.info(`★レンダリング - サイクルタイム改善`); // 再描画確認用

  // 選択された月のBOMサイクルタイムを取得（YYYY-MM → YYYY/MM形式に変換）、なければ0
  const bomCycleTime = useMemo(
    () => (item.month ? getCycleTimeFromBom(item.month.replace("-", "/")) : null) || 0,
    [item.month, getCycleTimeFromBom]
  );

  // BOMサイクルタイムを基準とした短縮幅（表示用）
  const diffText = useMemo(() => {
    const diff = (bomCycleTime - item.cycleTime).toFixed(1);
    return diff === "-0.0" ? "0.0" : diff;
  }, [bomCycleTime, item.cycleTime]);

  // タグの色（短縮幅の符号に応じて変更）
  const colorScheme = useMemo(() => {
    return diffText === "0.0" ? "gray" : parseFloat(diffText) > 0 ? "blue" : "red";
  }, [diffText]);

  return (
    <FormControl isInvalid={isInvalid}>
      <HStack>
        <Text>{t('features.InvestmentScreening.SimulationNewProduct.label.period')}</Text>
        <Input
          type="month"
          value={item.month}
          min={minMonth}
          max={maxMonth}
          onChange={(e) => onMonthChange(index, e.target.value)}
          style={{
            width: "150px",
            padding: "4px 8px",
            border: "1px solid #E2E8F0",
            borderRadius: "4px",
            fontSize: "14px",
            marginLeft: "8px",
          }}
        />
        <Text ml={2}>{t('features.InvestmentScreening.SimulationNewProduct.label.cycleTimeReduction')}</Text>
        <NumberInput
          width="90px"
          min={0.1}
          max={cycleTimeMax}
          precision={1}
          step={0.1}
          value={item.month ? item.cycleTimeText : "0.0"}
          clampValueOnBlur={false}
          onChange={(value) => onTextChange(index, value)}
          onBlur={() => onBlurCommit(index, item.month ? item.cycleTimeText : "0.0")}
        >
          <NumberInputField placeholder={t('features.InvestmentScreening.SimulationNewProduct.placeholder.seconds')} textAlign="right" />
          <NumberInputStepper>
            <NumberIncrementStepper />
            <NumberDecrementStepper />
          </NumberInputStepper>
        </NumberInput>
        <Text ml="2">{t('features.InvestmentScreening.SimulationNewProduct.label.seconds')}</Text>
        <Text ml="4" color="gray.600" fontSize="sm">
          {t('features.InvestmentScreening.SimulationNewProduct.label.cycleTimeBeforeInfo', {
            value: bomCycleTime.toFixed(1)
          })}
        </Text>
        <Tag
          ml="2"
          fontWeight="bold"
          borderRadius="full"
          variant="solid"
          colorScheme={colorScheme}
        >
          {t('features.InvestmentScreening.SimulationNewProduct.label.cycleTimeReductionInfo', {
            value: diffText
          })}
        </Tag>
        {isLast && (
          <IconButton
            aria-label="Add"
            icon={<FaPlusCircle />}
            colorScheme="blue"
            size="sm"
            ml={2}
            onClick={onAdd}
            isDisabled={!item.month}
          />
        )}
        {!isOnly && (
          <IconButton
            aria-label="Remove"
            icon={<FaCircleMinus />}
            colorScheme="red"
            size="sm"
            ml={isLast ? 0 : 2}
            onClick={() => onRemove(index)}
          />
        )}
      </HStack>
      {index === 0 && <FormErrorMessage>{errorMessage}</FormErrorMessage>}
    </FormControl>
  );
});

CycleTimeImprovementRow.displayName = "CycleTimeImprovementRow";

export default CycleTimeImprovementRow;
